import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { Mail, ArrowLeft, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rules = [
  { test: (p) => p.length >= 8, label: 'Mínimo 8 caracteres' },
  { test: (p) => /\d/.test(p), label: 'Pelo menos 1 número' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'Pelo menos 1 caractere especial' }
];

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const passOk = rules.every((r) => r.test(password)) && password === confirmPassword && password.length > 0;

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      toast.success('Se o email estiver cadastrado, você receberá um código.');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar o código.');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Digite o código de 6 dígitos.'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/verify-reset-code`, { email: email.trim().toLowerCase(), code: code.trim() });
      setResetToken(res.data.reset_token);
      toast.success('Código confirmado. Defina sua nova senha.');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido.');
    } finally {
      setLoading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!passOk) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        reset_token: resetToken,
        new_password: password,
        confirm_password: confirmPassword
      });
      toast.success('Senha alterada com sucesso! Faça login.');
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4 py-12" data-testid="forgot-password-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CardTitle>Recuperar senha</CardTitle>
          <CardDescription>
            {step === 1 && 'Informe o e-mail cadastrado para receber um código de 6 dígitos.'}
            {step === 2 && `Digite o código enviado para ${email}.`}
            {step === 3 && 'Defina a sua nova senha.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="forgot-email-form">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required className="pl-10" placeholder="seu@email.com" autoComplete="email"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading || !email.includes('@')} className="w-full bg-gradient-to-r from-amber-500 to-red-500 text-white" data-testid="forgot-email-submit">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</> : 'Enviar código'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitCode} className="space-y-4" data-testid="forgot-code-form">
              <div>
                <Label htmlFor="code">Código de 6 dígitos</Label>
                <Input
                  id="code" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required className="text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000" autoComplete="one-time-code"
                  data-testid="forgot-code-input"
                />
                <p className="text-xs text-slate-500 mt-2">Código válido por 15 minutos.</p>
              </div>
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-gradient-to-r from-amber-500 to-red-500 text-white" data-testid="forgot-code-submit">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando…</> : 'Confirmar código'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setStep(1)} className="w-full" data-testid="forgot-code-back">
                Reenviar / trocar e-mail
              </Button>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={submitPassword} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="new_password">Nova senha</Label>
                <PasswordInput id="new_password" leftIcon={Lock} value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                  data-testid="forgot-new-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirmar senha</Label>
                <PasswordInput id="confirm_password" leftIcon={Lock} value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password"
                  data-testid="forgot-confirm-password-input" />
              </div>
              <ul className="text-sm space-y-1">
                {rules.map((r, i) => {
                  const ok = r.test(password);
                  return <li key={i} className={ok ? 'text-emerald-600' : 'text-slate-400'}>{ok ? '✓' : '○'} {r.label}</li>;
                })}
                {password && confirmPassword && (
                  <li className={password === confirmPassword ? 'text-emerald-600' : 'text-red-500'}>
                    {password === confirmPassword ? '✓' : '✗'} Senhas coincidem
                  </li>
                )}
              </ul>
              <Button type="submit" disabled={!passOk || loading} className="w-full bg-gradient-to-r from-amber-500 to-red-500 text-white" data-testid="forgot-reset-submit">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Atualizando…</> : 'Redefinir senha'}
              </Button>
            </form>
          )}

          <div className="text-center mt-6">
            <Link to="/login" className="text-sm text-primary hover:underline flex items-center justify-center gap-1" data-testid="forgot-back-to-login">
              <ArrowLeft className="w-4 h-4" /> Voltar ao login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
